import React, { useMemo, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { ExpenseFormValues } from "../expense-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { VENDOR_PAYMENT_DETAILS_STEP } from "./constants";
import InitialStep from "./initial-step";
import CurrencyStep from "./currency-step";
import PaymentMethodFormStep, {
  VendorPaymentDetailsForm,
} from "./payment-method-form-step";
import { useGetCurrencies } from "@/services/queries/currency";
import { useGetVessels } from "@/services/queries/vessel";
import { InvoiceFormValues } from "../../invoices/components/invoice-form";

export type VendorPaymentDetailsFormValues =
  | ExpenseFormValues
  | InvoiceFormValues;

const VendorPaymentDetailsStep = ({
  form,
  setStep,
}: {
  form: UseFormReturn<VendorPaymentDetailsFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const [vendorPaymentDetailStep, setVendorPaymentDetailStep] =
    useState<number>(VENDOR_PAYMENT_DETAILS_STEP.INITAL);
  const { data: currenciesData, isPending: isCurrenciesPending } =
    useGetCurrencies();
  const { data: vesselsData } = useGetVessels();

  const currencies = useMemo(() => {
    return currenciesData?.value ?? [];
  }, [currenciesData?.value]);

  const vessels = useMemo(() => {
    return vesselsData?.value ?? [];
  }, [vesselsData?.value]);

  const vendorPaymentDetailSchema = z.object({
    iban: z.string().min(1),
    bankName: z.string().min(1),
    swiftCode: z.string().min(1),
    accountHolderName: z.string().min(1),
    currencyId: z.string().min(1),
    paymentTerms: z.string().optional(),
    bankAddress: z.string().optional(),
    bankAccountNumber: z.string().optional(),
    isDefault: z.boolean().optional(),
  });

  const vendorPaymentDetailForm = useForm<VendorPaymentDetailsForm>({
    resolver: zodResolver(vendorPaymentDetailSchema),
    defaultValues: {
      iban: "",
      bankName: "",
      swiftCode: "",
      accountHolderName: "",
      currencyId: form.watch("currencyId") ?? "",
      bankAddress: "",
      bankAccountNumber: "",
      paymentTerms: "",
    },
  });

  const onSubmit = (data: VendorPaymentDetailsForm) => {
    const existingPaymentMethods = form.getValues("vendorPaymentMethods") ?? [];

    // If the new payment method is default, set all existing ones to false
    const updatedExistingMethods = data.isDefault
      ? existingPaymentMethods.map((method) => ({
          ...method,
          isDefault: false,
        }))
      : existingPaymentMethods;

    const newVendorPaymentDetails = [
      ...updatedExistingMethods,
      {
        ...data,
        bankAddress: data.bankAddress ?? "",
        bankAccountNumber: data.bankAccountNumber ?? "",
      },
    ];

    form.setValue(
      "vendorPaymentMethods",
      newVendorPaymentDetails as VendorPaymentDetailsFormValues["vendorPaymentMethods"]
    );
    setVendorPaymentDetailStep(VENDOR_PAYMENT_DETAILS_STEP.INITAL);
    vendorPaymentDetailForm.reset();
  };

  const getStep = () => {
    switch (vendorPaymentDetailStep) {
      case VENDOR_PAYMENT_DETAILS_STEP.INITAL:
        return (
          <InitialStep
            form={form}
            setStep={setStep}
            setVendorPaymentDetailStep={setVendorPaymentDetailStep}
          />
        );

      case VENDOR_PAYMENT_DETAILS_STEP.PAYMENT_METHOD:
        return (
          <PaymentMethodFormStep
            form={vendorPaymentDetailForm}
            setStep={setVendorPaymentDetailStep}
            onSubmit={onSubmit}
            currencies={currencies}
          />
        );
      case VENDOR_PAYMENT_DETAILS_STEP.CURRENCY:
        return (
          <CurrencyStep
            form={vendorPaymentDetailForm}
            setStep={setVendorPaymentDetailStep}
            currencies={currencies}
            isCurrenciesPending={isCurrenciesPending}
            vessels={vessels}
            transactionDate={
              new Date(form.watch("transactionDate") ?? new Date())
            }
            vesselId={form.watch("vesselId")}
          />
        );
      default:
        return null;
    }
  };

  return <>{getStep()}</>;
};

export default VendorPaymentDetailsStep;
